import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSnackbar } from "notistack";
import { IconButton, Typography } from "@mui/material";
import { RestoreOutlined } from "@mui/icons-material";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { usePaymentIgnoreActions } from "../../hooks/usePaymentIgnoreActions.jsx";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import PaymentImportStatusBadge from "../PaymentImportStatusBadge.jsx";
import { colorsFoundation } from "@accredo/fe-shared/theme/index.js";
import {
  applyFilterWithAccessorKeys,
  applySortingWithAccessorKeys,
} from "./utils/tableUtils.js";
import PropTypes from "prop-types";

export default function PaymentImportPaymentsTable({
  paymentImportId,
  defaultRowsPerPage,
  onRowClick = () => {},
  reloadKey = 0,
  onReload = () => {},
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { restorePayments } = usePaymentIgnoreActions();

  const [activeTab, setActiveTab] = useState("active");
  const [counts, setCounts] = useState(null);

  const tableKey = "payments";
  const storageKey = "payment_import_payments";

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Zahlungen",
        route: `data/payment-imports/${paymentImportId}/payments`,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          ...(activeTab === "ignored" && { filters: { trashed: "only" } }),
        },
      },
    ],
    [paymentImportId, defaultRowsPerPage, activeTab]
  );

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  // shouldFetch must be passed explicitly: updateTableState skips the update
  // when no value changes, so { pageIndex: 0 } alone would never refetch.
  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    backendGet(`data/payment-imports/${paymentImportId}/payment-counts`, {
      label: "Zahlungsanzahl",
      setResult: (res) => setCounts(res?.data ?? null),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reloadKey, paymentImportId]);

  const filterTabs = useMemo(
    () =>
      [
        { key: "active", label: "Zahlungen", count: counts?.active },
        { key: "ignored", label: "Nicht relevant", count: counts?.ignored },
      ].map((tab) => ({ ...tab, onClick: setActiveTab })),
    [counts]
  );

  const columns = useMemo(
    () => [
      { accessorKey: "remitter", header: "Sender", size: 200 },
      { accessorKey: "description", header: "Beschreibung", size: 250 },
      {
        accessorKey: "iban_bic",
        accessorKeys: ["iban", "bic"],
        header: "Konto",
        size: 200,
        Cell: ({ row }) => {
          const iban = row.original.iban;
          const bic = row.original.bic;
          return (
            <Typography variant="body2">
              {iban} <br />
              <small>{bic}</small>
            </Typography>
          );
        },
      },
      {
        accessorKey: "amount",
        header: "Betrag",
        size: 100,
        Cell: ({ row, cell }) => {
          if (!row || !cell) {
            return null;
          }

          const amount = cell.getValue();
          const debitCredit = row.original.debit_credit;
          const isCredit = debitCredit === "C";
          const color = isCredit
            ? "green"
            : colorsFoundation.status.negative[500];
          const displayAmount = new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(isCredit ? amount : -amount);

          return (
            <span
              style={{
                color,
                fontWeight: "bold",
                textAlign: "right",
                display: "block",
              }}
            >
              {displayAmount}
            </span>
          );
        },
      },
      {
        accessorKey: "processed_at",
        header: "Datum",
        size: 100,
        Cell: ({ cell }) => {
          if (!cell) {
            return null;
          }
          const date = new Date(cell.getValue());
          return new Intl.DateTimeFormat("de-DE", {
            dateStyle: "short",
            timeStyle: "short",
          }).format(date);
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 140,
        Cell: ({ cell }) => {
          if (!cell) {
            return null;
          }
          const status = cell.getValue();

          return <PaymentImportStatusBadge status={status} />;
        },
      },
      ...(activeTab === "ignored"
        ? [
            {
              id: "actions",
              header: "Aktionen",
              size: 80,
              enableSorting: false,
              enableColumnFilter: false,
              Cell: ({ row }) =>
                row.original.can_restore ? (
                  <IconButton
                    size="small"
                    aria-label="Wiederherstellen"
                    title="Wiederherstellen"
                    onClick={(event) => {
                      event.stopPropagation();
                      restorePayments([row.original.id], { onDone: onReload });
                    }}
                  >
                    <RestoreOutlined fontSize="small" />
                  </IconButton>
                ) : null,
            },
          ]
        : []),
    ],
    [activeTab, restorePayments, onReload]
  );

  return (
    <DataTable
      columns={columns}
      data={tableState.rows || []}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangePage={(pagination) => updateTableState(tableKey, pagination)}
      onChangeSorting={(sorting) =>
        updateTableState(tableKey, {
          sorting: applySortingWithAccessorKeys(sorting, columns),
        })
      }
      onChangeFilters={(filters) =>
        updateTableState(tableKey, {
          filters: applyFilterWithAccessorKeys(filters, columns),
        })
      }
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      filterTabs={filterTabs}
      activeFilterTab={activeTab}
      onRowClick={onRowClick}
      toolbarActions={[
        {
          variant: "primary",
          label: " Zahlungen zuordnen",
          color: "primary",
          onClick: () =>
            navigate(
              `/admin/buchungen/imports/${paymentImportId}/payment-matching`
            ),
        },
      ]}
    />
  );
}

PaymentImportPaymentsTable.propTypes = {
  paymentImportId: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  defaultRowsPerPage: PropTypes.number.isRequired,
  onRowClick: PropTypes.func,
  reloadKey: PropTypes.number,
  onReload: PropTypes.func,
};
